import type EventBus from "services/eventBus";
import { serverError, userLogout } from "services/eventBus";
import extractErrorMessage from "utils/extractErrorMessage";
import { KnownErrors, SilentErrors } from "../config";

export interface CallApiOptions<TRequestBuilder> {
  useGenericErrorMessageHandler?: boolean;
  ownApiFactory?: () => TRequestBuilder;
}

export default abstract class RepositoryBase<TRequestBuilder> {
  constructor(protected apiFactory: () => TRequestBuilder, protected eventBus: EventBus) {}

  /**
   * Provides automated handling of errors and event publishing when calling API
   * @param action Action to be called on the API
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected callApi<T>(action: (api: TRequestBuilder) => Promise<T>, options?: CallApiOptions<TRequestBuilder>) {
    const useGenericErrorMessageHandler =
      options?.useGenericErrorMessageHandler !== undefined ? options.useGenericErrorMessageHandler : true;
    const apiFactory = options?.ownApiFactory !== undefined ? options.ownApiFactory : this.apiFactory;
    const promise = action(apiFactory());

    promise.catch(error => {
      if (error.response?.status === 401) {
        this.eventBus.publish(userLogout());
      }

      if (useGenericErrorMessageHandler) {
        const type = error.content?.type;
        const status = error.response?.status;

        if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
          // Requested was aborted - silent catch
        } else if (type && SilentErrors.hasOwnProperty(type)) {
          // do nothing
        } else if (type && KnownErrors.hasOwnProperty(type)) {
          this.eventBus.publish(serverError({ type }));
        } else if (status && status >= 400 && status < 500) {
          if (status === 413) {
            this.eventBus.publish(serverError({ type: "payloadTooLarge" }));
          } else {
            const message = extractErrorMessage(error);
            this.eventBus.publish(serverError({ message }));
          }
        } else if (status && status >= 500) {
          this.eventBus.publish(serverError({ type: "internalServerError" }));
        } else {
          this.eventBus.publish(serverError({}));
        }
      }
    });

    return promise;
  }
}
